import { Query, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import {
  DataQueryKey,
  UseDataQueryKey,
} from '../../../../../../../../hooks/useDataQuery/dataQuery.types';
import { AvailabilityEnum } from '../../../../../../../products/ProductTypes';
import orderMaterialRepository from '../../../../../../repositories/orderMaterial.repository';

const useRelatedMaterials = () => {
  const queryClient = useQueryClient();

  const getRelatedProductColorIds = useCallback(
    async (orderId: string, productColorIds: string[]) => {
      const repository = orderMaterialRepository(orderId);
      const response = await repository.getProductColorsSharingMaterial(productColorIds);
      if (response.ok && response.data) {
        return response.data.productColorIds;
      }
      return [];
    },
    [],
  );

  const refetchProductColors = useCallback(
    async (orderId: string, productColorIds: string[]) => {
      const matchesQueryPredicate = (query: Query): boolean => {
        const [, dataQueryKey, productColorIdKey, params] = query.queryKey as UseDataQueryKey;
        const mustInvalidateQuery =
          dataQueryKey === DataQueryKey.PRODUCT_GRID &&
          params.orderId === orderId &&
          params.availability === AvailabilityEnum.MATERIAL_PRE_ORDER &&
          productColorIds.includes(productColorIdKey);

        return mustInvalidateQuery;
      };

      queryClient.invalidateQueries({
        type: 'active',
        predicate: matchesQueryPredicate,
      });

      queryClient.removeQueries({
        type: 'inactive',
        predicate: matchesQueryPredicate,
      });
    },
    [queryClient],
  );

  const refetchProductColorsSharingMaterial = useCallback(
    async (orderId: string, productColorIds: string[]) => {
      const relatedProductColorIds = await getRelatedProductColorIds(orderId, productColorIds);
      refetchProductColors(orderId, relatedProductColorIds);
    },
    [getRelatedProductColorIds, refetchProductColors],
  );

  return {
    getRelatedProductColorIds,
    refetchProductColors,
    refetchProductColorsSharingMaterial,
  };
};

export default useRelatedMaterials;
